import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { NoDataPlaceHolderTableRow } from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import EntityTableHeader from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import { EmptyAdjustmentContentScore } from 'src/components/SvgIcons';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useMainTimePeriodShortDisplayName, useMetricFormatter } from 'src/utils/Hooks';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useMainEntityName, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import saveAs from 'file-saver';
import { useContentService } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useContentService';
import { KeywordType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { FIVE_MIN } from 'src/utils/constants';

const entityMask = '***';
const periodMask = '###';
const CLIENT_SUB_TITLE = `The table below highlights keywords that could be added to optimize the product pages
  for ${entityMask}. The data represents organic traffic during the ${periodMask} period.`;
const PRODUCT_SUB_TITLE = `The table below highlights keywords that could be added to optimize this product page. The data represents organic traffic during the ${periodMask} period.`;

export const KeywordOpportunityConstants = {
  TABLE_TITLE: 'Keyword Opportunities',
  COLUMN_HEADERS: {
    KEYWORD: 'KEYWORD',
    TYPE: 'TYPE',
    KEYWORD_USED_STATUS: 'KEYWORD USED STATUS',
    ORGANIC_TRAFFIC_OPPORTUNITY: 'ORGANIC TRAFFIC OPPORTUNITY',
    ORGANIC_TRAFFIC_UNEARNED: 'ORGANIC TRAFFIC UNEARNED',
    ORGANIC_TRAFFIC_SHARE_EARNED: 'ORGANIC TRAFFIC SHARE EARNED'
  },
  CSV_FILENAME: 'KeywordOpportunities.csv',
  NO_DATA: {
    TITLE: 'No keyword opportunities found.',
    DESCRIPTION: 'There are currently no keyword opportunities to display.'
  }
};

export const handleCsvDownload = (formattedData: string[][], fileName = 'Adjustments.csv') => {
  const escapeDoubleQuotes = (field: string) => {
    return `"${field.replace(/"/g, '""')}"`;
  };

  const csvContent = formattedData.map((row) => row.map(escapeDoubleQuotes).join(',')).join('\n');

  try {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob as Blob, fileName);
  } catch (e) {
    console.error('Error exporting chart:', e);
  }
};

// Notes: Figma 120px gap, will wrapping the keyword collumn, so we need reduce it to 80px
const createSpacerColumn = (fieldName: string, width = '80px') => ({
  headerName: '',
  field: fieldName,
  tableColumnProps: {
    style: {
      width,
      padding: '0px',
      borderLeft: 'none',
      borderRight: 'none'
    }
  },
  valueFormatter: () => {
    return <div style={{ width }}></div>;
  }
});

const KeywordOppotunityTable = () => {
  const formatMetric = useMetricFormatter();
  const { getKeywordOpportunities } = useContentService();
  const entityName = useMainEntityName();
  const mainTimePeriodName = useMainTimePeriodShortDisplayName();
  const entityType = useMainEntityType();
  const { COLUMN_HEADERS } = KeywordOpportunityConstants;

  const { data: keywordData = [], isLoading } = useQuery({
    queryKey: ['keywordOpportunities-${entityName}-${mainTimePeriodName}-${entityType}'],
    queryFn: async () => {
      try {
        const data = await getKeywordOpportunities();

        if (data && data.length > 0) {
          return data
            .filter((item) => !item.is_keyword_used)
            .sort((a, b) => b.organic_traffic_opportunity - a.organic_traffic_opportunity)
            .map((item, index) => ({
              key: index.toString(),
              keyword: item.keyword,
              // Default to Unbranded as the API doesn't provide this info
              type: item.is_branded ? KeywordType.Branded : KeywordType.Unbranded,
              organicTrafficOpportunity: formatMetric(item.organic_traffic_opportunity, METRICTYPE.VOLUME),
              organicTrafficUnearned: formatMetric(item.organic_traffic_unearned, METRICTYPE.VOLUME),
              organicTrafficShareEarned: formatMetric(item.organic_traffic_share_earned, METRICTYPE.PERCENT, {
                decimalPlaces: 2
              })
            }));
        }

        return [];
      } catch (error) {
        console.error('Error fetching keyword opportunities:', error);
        return [];
      }
    },
    retry: 0,
    refetchOnWindowFocus: false,
    staleTime: FIVE_MIN
  });

  const columnDefs = [
    {
      headerName: COLUMN_HEADERS.KEYWORD,
      field: 'keyword',
      tableColumnProps: {
        style: {
          width: 'auto',
          paddingRight: '16px'
        }
      },
      valueFormatter: (value: string) => {
        return <Text variant="body2">{value}</Text>;
      }
    },
    createSpacerColumn('spacer1'),
    {
      headerName: COLUMN_HEADERS.TYPE,
      field: 'type',
      tableColumnProps: {
        style: {
          width: '69px',
          padding: '0px'
        }
      },
      valueFormatter: (value: string) => {
        return <Text variant="body2">{value}</Text>;
      }
    },
    createSpacerColumn('spacer2'),
    {
      headerName: COLUMN_HEADERS.ORGANIC_TRAFFIC_OPPORTUNITY,
      field: 'organicTrafficOpportunity',
      tableColumnProps: {
        style: {
          width: '120px',
          padding: '0px'
        }
      },
      valueFormatter: (value: number) => {
        return <Text variant="body2">{value}</Text>;
      }
    },
    createSpacerColumn('spacer3'),
    {
      headerName: COLUMN_HEADERS.ORGANIC_TRAFFIC_UNEARNED,
      field: 'organicTrafficUnearned',
      tableColumnProps: {
        style: {
          width: '120px',
          padding: '0px'
        }
      },
      valueFormatter: (value: number) => {
        return <Text variant="body2">{value}</Text>;
      }
    },
    createSpacerColumn('spacer4'),
    {
      headerName: COLUMN_HEADERS.ORGANIC_TRAFFIC_SHARE_EARNED,
      field: 'organicTrafficShareEarned',
      tableColumnProps: {
        style: {
          width: '120px',
          padding: '0px'
        }
      },
      valueFormatter: (value: string) => {
        return <Text variant="body2">{value}</Text>;
      }
    }
  ];

  const generateCSVData = () => {
    const headers = [
      COLUMN_HEADERS.KEYWORD,
      COLUMN_HEADERS.TYPE,
      COLUMN_HEADERS.ORGANIC_TRAFFIC_OPPORTUNITY,
      COLUMN_HEADERS.ORGANIC_TRAFFIC_UNEARNED,
      COLUMN_HEADERS.ORGANIC_TRAFFIC_SHARE_EARNED
    ];

    const rows = keywordData.map((item) => [
      item.keyword,
      item.type,
      item.organicTrafficOpportunity.toString(),
      item.organicTrafficUnearned.toString(),
      item.organicTrafficShareEarned
    ]);

    return [headers, ...rows];
  };

  const formattedExportData = generateCSVData();

  return (
    <Flex flexDirection="column">
      <EntityTableHeader
        prefixTitle={KeywordOpportunityConstants.TABLE_TITLE}
        title={KeywordOpportunityConstants.TABLE_TITLE}
        subTitle={() => (
          <Text variant="body2" sx={{ marginTop: '-12px', fontWeight: '400', fontSize: '16px', marginBottom: '32px' }}>
            {entityType === 'product'
              ? PRODUCT_SUB_TITLE.replace(periodMask, mainTimePeriodName)
              : CLIENT_SUB_TITLE.replace(entityMask, entityName).replace(periodMask, mainTimePeriodName)}
          </Text>
        )}
        paddingBottom="20px"
        enabledActions={['ExportCsv']}
        handleCSVExport={() => handleCsvDownload(formattedExportData, KeywordOpportunityConstants.CSV_FILENAME)}
        rowData={keywordData}
        defaultView="list"
      />
      <EntityTable
        onRowClick={() => {}}
        isLoading={isLoading}
        rowData={keywordData}
        columnDefs={columnDefs}
        shouldModifyColumnDefs={false}
        supportViewMore
        initialPageSize={10}
        shouldPaginateViewMore
        appTableProps={{
          sx: {
            '& th[data-field*="spacer"], & td[data-field*="spacer"]': {
              borderLeft: 'none !important',
              borderRight: 'none !important',
              borderTop: 'none !important',
              borderBottom: 'none !important'
            },
            '& thead th': {
              paddingBottom: '16px !important'
            },
            '& tbody tr': {
              cursor: 'default !important'
            }
          },
          noDataPlaceholder: (
            <NoDataPlaceHolderTableRow
              icon={<EmptyAdjustmentContentScore />}
              title={KeywordOpportunityConstants.NO_DATA.TITLE}
              description={KeywordOpportunityConstants.NO_DATA.DESCRIPTION}
            />
          )
        }}
      />
    </Flex>
  );
};

export default KeywordOppotunityTable;
